import { DeviceHub } from '@mui/icons-material';
import { Box, Button, Chip, MenuItem, Select, type DialogProps } from '@mui/material';
import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAddToRole } from '~/api/roles';
import { Tooltip } from '~/components/Tooltip';
import { SeparatedDataGrid, hiddenHeader } from '~/components/data-grid';
import { DeviceIndicators } from '~/components/devices';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { SearchBar } from '~/components/forms/search-bar';
import { ListCheckbox } from '~/components/list-row';
import { RuleAccess } from '~/generated/graphql';
import { searchRegex } from '~/lib/search';
import { getRuleAccessLabel } from '../../lib';
import type {
  RoleDeviceList__Device,
  RoleDeviceList__DeviceGroup,
  RoleDeviceList__DeviceGroupRule,
  RoleDeviceList__DeviceRule,
  RoleDeviceList__Role,
} from '../RoleDeviceList.generated';
import { MultiCount } from './MultiCount';

const itemId = (item: RoleDeviceList__DeviceGroup | RoleDeviceList__Device): string =>
  `${item.__typename}:${item.id}`;

const ruleItemId = (rule: RoleDeviceList__DeviceGroupRule | RoleDeviceList__DeviceRule): string =>
  rule.__typename === 'DeviceRule' ? itemId(rule.device) : itemId(rule.deviceGroup);

export interface AddDevicesDialogProps extends Omit<DialogProps, 'role'> {
  deviceGroups: readonly RoleDeviceList__DeviceGroup[];
  devices: readonly RoleDeviceList__Device[];
  role: RoleDeviceList__Role;
}

export const AddDevicesDialog = ({
  deviceGroups,
  devices,
  role,
  ...props
}: AddDevicesDialogProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  const [accessValues, setAccessValues] = useState<Record<string, RuleAccess | undefined>>({});

  const handleAccessChange = (id: string, access: RuleAccess) => {
    setAccessValues((prev) => ({ ...prev, [id]: access }));
  };

  const deviceGroupRules = selectedIds
    .filter((id) => String(id).startsWith('DeviceGroupNew:'))
    .map((id) => ({
      deviceGroupId: Number(String(id).split(':')[1]),
      access: accessValues[id] || RuleAccess.Write,
    }));

  const deviceRules = selectedIds
    .filter((id) => String(id).startsWith('Device:'))
    .map((id) => ({
      deviceId: Number(String(id).split(':')[1]),
      access: accessValues[id] || RuleAccess.Write,
    }));

  const [addToRole, { called }] = useAddToRole({
    variables: { roleId: role.id, deviceGroupRules, deviceRules },
  });

  const itemIds = new Set<GridRowId>(
    [...role.deviceGroupRules, ...role.deviceRules].map(ruleItemId),
  );

  const searchReg = searchRegex(search);

  const rows = [...deviceGroups, ...devices].filter(
    (item) =>
      !itemIds.has(itemId(item)) && (searchReg.test(String(item.id)) || searchReg.test(item.name)),
  );

  // Sucks
  const close = () => props.onClose?.({}, 'backdropClick');

  return (
    <Dialog {...props}>
      <DialogTitle onClose={close}>{t('pages.roles.dialogs.add_devices.title')}</DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DialogContentTitle>
          <Trans
            i18nKey="pages.roles.dialogs.add_devices.instruction"
            values={{ role_name: role.name }}
            components={{ b: <b /> }}
          />
        </DialogContentTitle>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SearchBar
            onChange={setSearch}
            placeholder={t('pages.roles.dialogs.add_devices.search_placeholder')}
            search={search}
          />
          <MultiCount
            descriptors={[
              {
                matchesItem: (item) => item.__typename === 'DeviceGroupNew',
                matchesSelectedId: (id) => String(id).startsWith('DeviceGroupNew:'),
                name: 'pages.roles.dialogs.add_devices.device_group_plural',
              },
              {
                matchesItem: (item) => item.__typename === 'Device',
                matchesSelectedId: (id) => String(id).startsWith('Device:'),
                name: 'pages.roles.dialogs.add_devices.device_plural',
              },
            ]}
            items={rows}
            selectedIds={selectedIds}
          />
        </Box>

        <Box>
          <SeparatedDataGrid
            autoHeight={false}
            checkboxSelection
            columns={[
              {
                align: 'center',
                headerAlign: 'center',
                field: 'type',
                ...hiddenHeader(t('common.labels.type')),
                width: 150,
                renderCell: ({ row }) =>
                  row.__typename === 'Device' ? (
                    <DeviceIndicators device={row} />
                  ) : (
                    <Tooltip
                      describeChild={false}
                      title={t('pages.roles.dialogs.add_devices.device_group_plural', { count: 1 })}
                    >
                      <DeviceHub
                        color="primary"
                        titleAccess={t('pages.roles.dialogs.add_devices.device_group_plural', {
                          count: 1,
                        })}
                      />
                    </Tooltip>
                  ),
                sortable: false,
              },
              { field: 'name', headerName: t('common.labels.name'), flex: 1 },
              {
                field: 'access',
                headerName: t('pages.roles.devices.columns.access'),
                renderCell: ({ row }) => {
                  const id = itemId(row);
                  return (
                    <Select
                      disabled={!selectedIds.includes(id)}
                      fullWidth
                      name="access"
                      size="small"
                      value={accessValues[id] || RuleAccess.Write}
                      onChange={(e) => handleAccessChange(id, e.target.value as RuleAccess)}
                    >
                      <MenuItem value={RuleAccess.Read}>
                        {getRuleAccessLabel(RuleAccess.Read, t)}
                      </MenuItem>
                      <MenuItem value={RuleAccess.Write}>
                        {getRuleAccessLabel(RuleAccess.Write, t)}
                      </MenuItem>
                    </Select>
                  );
                },
                width: 150,
              },
              {
                field: 'devices',
                headerName: t('common.labels.devices'),
                align: 'center',
                headerAlign: 'center',
                renderCell: ({ row }) =>
                  row.__typename === 'Device' ? (
                    '--'
                  ) : (
                    <Chip color="primary" label={row.deviceCount} />
                  ),
              },
            ]}
            getRowId={(row) => `${row.__typename}:${row.id}`}
            onRowSelectionModelChange={setSelectedIds}
            rowSelectionModel={selectedIds}
            rows={rows}
            slots={{
              baseCheckbox: ListCheckbox,
              columnResizeIcon: () => null,
              noRowsOverlay: () => {
                return (
                  <Box height="100%" display="flex" justifyContent="center" alignItems="center">
                    {t('pages.roles.dialogs.add_devices.no_devices')}
                  </Box>
                );
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button disabled={called} onClick={close} variant="outlined">
          {t('common.buttons.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={called || !selectedIds.length}
          onClick={() => {
            close();
            return addToRole();
          }}
          variant="contained"
        >
          {t('pages.roles.buttons.add_devices')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
